import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_BASE_URL;

// -------------------- ASYNC ACTIONS --------------------

// Fetch all documents for a specific employee
export const getDocumentsByEmployee = createAsyncThunk(
  "governmentDocuments/getByEmployee",
  async (employeeId, { rejectWithValue }) => {
    try {
      
      const res = await axios.get(
        `${API_URL}/government-documents/employee/${employeeId}`,
      );
      return res.data.map((d) => ({
        docId: d.id,
        docType: d.doc_type,
        docName: d.docName,
        idNumber: d.id_number,
        isValidated: d.isValidated,
        file: d.file_url,
        customName: d.custom_name,
      }));
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const createDocument = createAsyncThunk(
  'governmentDocuments/create',
  async ({ employeeId, data }, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${API_URL}/government-documents/employee/${employeeId}`, data);
      const d = res.data;
      console.log(d)
      return {
        docId: d.id,
        docType: d.doc_type,
        docName: d.docName,
        idNumber: d.id_number,
        file: d.file_url,
        customName: d.custom_name,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const updateDocument = createAsyncThunk(
  "governmentDocuments/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await axios.patch(
        `${API_URL}/government-documents/${id}`,
        data,
      );
      const d = res.data;
      return {
        docId: d.id,
        docType: d.doc_type,
        docName: d.docName,
        idNumber: d.id_number,
        file: d.file_url,
        customName: d.custom_name,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const uploadDocumentFile = createAsyncThunk(
  "governmentDocuments/uploadFile",
  async ({ id, file, companyId }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      // We pass companyId in the URL as a query param
      const response = await axios.post(
        `${API_URL}/government-documents/${id}/upload?companyId=${companyId}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      return { url: response.data.file_url, id: id };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Upload failed");
    }
  },
);

export const deleteDocument = createAsyncThunk(
  "governmentDocuments/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_URL}/government-documents/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

// Check Employee Completeness
export const checkEmployeeCompleteness = createAsyncThunk(
  "governmentDocuments/checkCompleteness",
  async (employeeId, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`${API_URL}/employee/${employeeId}/check-completeness`);
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Completeness check failed",
      );
    }
  },
);

// Fetch the clickable Zoho Permalink
export const getDocumentViewLink = createAsyncThunk(
  "governmentDocuments/getViewLink",
  async ({ docId }, { rejectWithValue }) => {
    try {
      // CHANGE THIS LINE to match your working curl path:
      const res = await axios.get(
        `${API_URL}/bulk-action/view-document/${docId}`,
      );

      return { docId, viewUrl: res.data.url || res.data };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const validateDoc = createAsyncThunk(
  "governmentDocuments/validateDoc",
  async ({ docId }, { rejectWithValue }) => {
    try {
      // CHANGE THIS LINE to match your working curl path:
      const res = await axios.patch(
        `${API_URL}/government-documents/${docId}/gov-docs-val`,
      );

      return { docId, result: res.data };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);


// -------------------- SLICE --------------------

const initialState = {
  documents: [],
  status: "idle",
  completenessStatus: "idle", // Track completeness separately
  error: null,
};

const governmentDocumentSlice = createSlice({
  name: "governmentDocument",
  initialState,
  reducers: {
    clearDocuments(state) {
      state.documents = [];
      state.status = "idle";
      state.completenessStatus = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* Get Documents */
      .addCase(getDocumentsByEmployee.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.documents = action.payload;
      })
      /* Create Document */
      .addCase(createDocument.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.documents.push(action.payload);
      })
      /* Update Document */
      .addCase(updateDocument.fulfilled, (state, action) => {
        state.status = "succeeded";
        const index = state.documents.findIndex(
          (d) => d.docId === action.payload.docId,
        );
        if (index !== -1) state.documents[index] = action.payload;
      })
      /* Delete Document */
      .addCase(deleteDocument.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.documents = state.documents.filter(
          (d) => d.docId !== action.payload,
        );
      })
      /* Upload File */
      .addCase(uploadDocumentFile.fulfilled, (state, action) => {
        state.status = "succeeded";
        const index = state.documents.findIndex(
          (d) => d.docId === action.meta.arg.id,
        );
        if (index !== -1) state.documents[index].file = action.payload.url;
      })
      /* Completeness Check */
      .addCase(checkEmployeeCompleteness.pending, (state) => {
        state.completenessStatus = "loading";
      })
      .addCase(checkEmployeeCompleteness.fulfilled, (state) => {
        state.completenessStatus = 'succeeded';
      })
      .addCase(checkEmployeeCompleteness.rejected, (state, action) => {
        state.completenessStatus = "failed";
        state.error = action.payload;
      })
      .addCase(getDocumentViewLink.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getDocumentViewLink.fulfilled, (state, action) => {
        state.status = "succeeded";
        const index = state.documents.findIndex(
          (d) => d.docId === action.payload.docId,
        );
        if (index !== -1) {
          // Store the fetched URL on the document object
          state.documents[index].viewUrl = action.payload.viewUrl;
        }
      })
      .addCase(getDocumentViewLink.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      /* Validate Document */
      .addCase(validateDoc.pending, (state) => {
        state.status = "loading";
      })
      .addCase(validateDoc.fulfilled, (state, action) => {
        state.status = "succeeded";
        const index = state.documents.findIndex(
          (d) => d.docId === action.payload.docId,
        );
        if (index !== -1) {
          // Update the validation status in the store
          // Ensure the key matches what your UI uses (e.g., isValidated)
          state.documents[index].isValidated = true;

          // If the validation also returns a special view URL, store it too
          if (action.payload.viewUrl) {
            state.documents[index].viewUrl = action.payload.viewUrl;
          }
        }
      })
      .addCase(validateDoc.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      /* Global Matchers for main CRUD actions */
      .addMatcher(
        (action) =>
          action.type.endsWith("/pending") &&
          !action.type.includes("checkCompleteness"),
        (state) => {
          state.status = "loading";
          state.error = null;
        },
      )
      .addMatcher(
        (action) =>
          action.type.endsWith("/rejected") &&
          !action.type.includes("checkCompleteness"),
        (state, action) => {
          state.status = "failed";
          state.error = action.payload;
        },
      );
  },
});

export const { clearDocuments } = governmentDocumentSlice.actions;
export default governmentDocumentSlice.reducer;
